import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronRight } from '@mui/icons-material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { Grid, Paper } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getJobStatsPostedOnJobBoards,
  IJobBoard,
  postToCWJobs,
  ICWJobsJobPostDTO,
  getCWJobsLocations,
} from 'wisestep-api';
import {
  WsButton,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsRadioGroup,
  IUserDTO,
  WsAutocompleteInput,
  WsSelect,
} from 'wisestep-base';
import { object, string } from 'yup';

import NoDataFoundCard from '../../../../../../components/noDataFoundCard';
import { useUserContext } from '../../../../../../context';
import CWJobsIcon from '../../../../../../images/jobBoardsIcons/CWJob.png';
import { useNotificationStore } from '../../../../../../utils/NotificationStore';
import JobPageParams from '../../../../types/JobPageParams';
import {
  getJobBoards,
  publishCreditsWarning,
  publishFailed,
  publishSuccess,
} from '../../helper/jobBoardHelper';
import SharedUserLists from '../SharedUserLists';

const validationSchema = {
  region: string().required('Please enter a region'),
  isEuOnly: string().required('Please Select Mandatory Fields'),
  liveDays: string().required('Please Select Mandatory Fields'),
};
interface ICWCount {
  CWJobsCount: number;
}
interface ICWJobsAccountState {
  CWJobsResponse: IJobBoard[];
  emailsData: IJobBoard[];
  jobStatsPostedOnJobBoard?: ICWCount;
}

interface ICWJobsPostingFrom {
  jobBoardId: string;
  region: string;
  liveDays: string;
  isEuOnly: string;
}

const CWJobsPostingAccount = () => {
  const [CWJobsAccountState, setCWJobsAccountState] = useState<ICWJobsAccountState>({
    CWJobsResponse: [],
    emailsData: [],
  });
  const setNotification = useNotificationStore((state) => state.setNotification);
  const params = useParams<JobPageParams>();
  const jobId = params.jobId;
  const navigate = useNavigate();
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const CWJobsPayload: ICWJobsJobPostDTO = {
    jobIds: [jobId ?? ''],
    jobBoardId: '',
  };

  const formOptions = {
    resolver: yupResolver(object().shape(validationSchema)),
  };

  const methods = useForm<ICWJobsPostingFrom>(formOptions);
  const { handleSubmit, setValue } = methods;

  useEffect(() => {
    const setCWjobsBoard = async () => {
      const allAccounts = await getJobBoards('CWJobs');
      const CWJobsAccounts = allAccounts.filter(
        (account: IJobBoard) => account.jobBoardName === 'CWJobs'
      );
      setCWJobsAccountState((prev) => ({
        ...prev,
        CWJobsResponse: CWJobsAccounts,
        emailsData:
          CWJobsAccounts?.filter((account) => account.id === CWJobsAccounts?.[0].id) ?? [],
      }));
      setValue('jobBoardId', CWJobsAccounts?.[0].id);
    };
    setCWjobsBoard();
    getJobStatsPostedOnJobBoard();
  }, [setValue]);

  const isEuOnlyOptions = [
    {
      label: 'Yes',
      value: 'True',
    },
    {
      label: 'No',
      value: 'False',
    },
  ];

  const jobBoardAccounts = CWJobsAccountState.CWJobsResponse?.filter(
    (account) =>
      account.jobBoardSharedUsers &&
      account.jobBoardSharedUsers?.filter(
        (sharedAccount) => sharedAccount.email == loggedInUser.email
      ).length > 0
  ).map((account) => ({
    label: account.username,
    value: account.id ?? '',
  }));

  const getJobStatsPostedOnJobBoard = async () => {
    const response = await getJobStatsPostedOnJobBoards();
    setCWJobsAccountState((prev) => ({
      ...prev,
      jobStatsPostedOnJobBoard: response.data,
    }));
  };

  const onFormSubmit = async (data: ICWJobsPostingFrom) => {
    const selectAccount: IJobBoard[] = CWJobsAccountState.emailsData;
    if (selectAccount.length > 0 && loggedInUser) {
      const maxPostingAvailableCount = selectAccount[0].jobBoardSharedUsers?.filter(
        (e) => e.email == loggedInUser.email
      )[0]?.maxPostingsPerMonth;
      if (
        !maxPostingAvailableCount ||
        (CWJobsAccountState.jobStatsPostedOnJobBoard &&
          CWJobsAccountState.jobStatsPostedOnJobBoard?.CWJobsCount >= maxPostingAvailableCount)
      ) {
        setNotification(publishCreditsWarning());
        return;
      }
    }
    try {
      CWJobsPayload.jobBoardId = data.jobBoardId;
      CWJobsPayload.liveDays = data.liveDays ?? '28';
      CWJobsPayload.isEuOnly = data.isEuOnly;
      CWJobsPayload.region = data.region ?? 'Other';

      if (jobId) await postToCWJobs(CWJobsPayload);
      setNotification(publishSuccess());
      navigate(`/jobs/${jobId}/publish`);
    } catch {
      setNotification(publishFailed());
    }
  };

  const onLocationInput = async (query: string) => {
    const response = await getCWJobsLocations(query);
    return response.data;
  };

  const liveDaysOptions = [
    {
      label: '7 Days',
      value: '7',
    },
    {
      label: '14 Days',
      value: '14',
    },
    {
      label: '21 Days',
      value: '21',
    },
    {
      label: '28 Days',
      value: '28',
    },
    {
      label: '35 Days',
      value: '35',
    },
    {
      label: '42 Days',
      value: '42',
    },
  ];

  return (
    <Paper sx={{ pt: 2 }}>
      <Grid item xs={6} container columnGap={2}>
        <ChevronLeft onClick={() => navigate(`/jobs/${jobId}/publish`)} />
        <img src={CWJobsIcon} alt="CWJobs" height="35" />
      </Grid>
      {jobBoardAccounts.length > 0 ? (
        <Grid>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <Paper sx={{ p: 2 }}>
                <Grid container p={3}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Choose an account *
                    </WsTypography>
                    <WsButton
                      variant="text"
                      size="large"
                      onClick={() => navigate('/job-boards/cwJobs-jobposting')}
                    >
                      Configure accounts
                    </WsButton>
                  </Grid>
                  {jobBoardAccounts && (
                    <Grid item xs={4} pl={1}>
                      <WsRadioGroup
                        name="jobBoardId"
                        label=""
                        selectArray={jobBoardAccounts ?? []}
                        row={false}
                        size="medium"
                      />
                    </Grid>
                  )}
                  <Grid container direction="row" spacing={2} item xs={12} pb={1}>
                    <Grid item xs={8}>
                      <WsAutocompleteInput
                        label="Region"
                        getOptions={onLocationInput}
                        options={[]}
                        placeholder={'Region'}
                        multiple={false}
                        freeSolo={false}
                        name="region"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <WsSelect
                        name="liveDays"
                        label="No of Active days"
                        selectArray={liveDaysOptions}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid item xs={3}>
                      <WsRadioGroup
                        name="isEuOnly"
                        label="Post Job in EU Only"
                        selectArray={isEuOnlyOptions}
                        row={true}
                        size="medium"
                        required={true}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Grid container justifyContent="flex-end">
                  <WsButton
                    variant="contained"
                    type="submit"
                    size="large"
                    endIcon={<ChevronRight />}
                  >
                    Publish
                  </WsButton>
                </Grid>
              </Paper>
            </form>
          </FormProvider>
          <Grid container item xs={12}>
            {CWJobsAccountState.emailsData && CWJobsAccountState.emailsData.length > 0 && (
              <SharedUserLists
                value={CWJobsAccountState.emailsData[0].username}
                selectedAccount={CWJobsAccountState.emailsData}
                usedCount={CWJobsAccountState.jobStatsPostedOnJobBoard?.CWJobsCount ?? 0}
              />
            )}
          </Grid>
        </Grid>
      ) : (
        <NoDataFoundCard text="No Job posting credits allocated to your account, Contact Admin" />
      )}
    </Paper>
  );
};
export default CWJobsPostingAccount;
